import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Check as CheckIcon, Close as CloseIcon, InfoOutlined as InfoIcon, WarningAmber as WarningIcon } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { INDIGO, SLATE } from "../../constants/Colors";
import { usePageMeta } from "../../contexts/PageMetaContext";
import useApiCall from "../../hooks/useApiCall";
import usePostRequest from "../../hooks/usePostRequest";
import useFetch from "../../hooks/useFetch";
import LoadingSpinner from "../../components/LoadingSpinner";
import BottomActionBar from "../../components/BottomActionBar";
import OptionMenu from "../../components/OptionMenu";
import UrlsSidebar from "./Components/UrlsSidebar";
import UnifiedUrlsSidebar from "./Components/UnifiedUrlsSidebar";
import { StatusFilterMenu, StatusChip } from "../../components/status";
import SearchInput from "../../components/SearchInput";
import TrimTextField from "../../components/TrimTextField";
import {
  generateTempId,
  removeDuplicatesFromArray,
  yearsListSince,
} from "../../utils";

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    bgcolor: "#ffffff",
    "& fieldset": { borderColor: SLATE[300] },
    "&:hover fieldset": { borderColor: SLATE[400] },
    "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
  },
  "& .MuiInputBase-input": { color: SLATE[900] },
};

const FIELD_LABEL_SX = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: SLATE[700],
  mb: "6px",
  display: "block",
};

const COL_TEMPLATE = "minmax(220px, 2fr) 60px 140px minmax(160px, 200px) minmax(100px, 1fr) minmax(100px, 1fr) minmax(100px, 1fr) 48px";

const SCAN_MODE_OPTIONS = [
  { title: "Full Website", value: "full_website" },
  { title: "Few URLs", value: "few_urls" },
];

const EMPTY_FORM = {
  url: "",
  site_map: "full_website",
  tags: [],
  skip_patterns: [],
  allowed_patterns: [],
};

export default function Websites() {
  const user = useSelector((state) => state.user.value);
  const tokens = useSelector((state) => state?.tokens.value);
  const params = useParams();
  const currentApp = useSelector((state) => state?.apps?.selectedApp);
  const selectedAgentSiteCode = useSelector((state) => state?.apps?.selectedAgentSiteCode);
  const { makeRequest } = useApiCall();
  const { setPageMeta } = usePageMeta();

  const agentId = params.id || null;

  // For legacy routes /:app_name/agent/:site_code/* the agentId IS the site_code.
  // For new routes /:app_name/agent/:numeric_id/* the site_code comes from Redux
  // (dispatched by Agents/ManageAgent/AgentTypeSelector before navigating here).
  const isNumericId = agentId && /^\d+$/.test(agentId);
  const agentSiteCode = isNumericId ? selectedAgentSiteCode : agentId;

  const [queryParams, setQueryParams] = useState({
    page: 1,
    pageSize: 25,
    searchQuery: "",
    refreshKey: 0,
  });
  const [websites, setWebsites] = useState([]);
  const [changes, setChanges] = useState({});
  const [saved, setSaved] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [statusCounts, setStatusCounts] = useState({});

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWebsite, setEditingWebsite] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  // Inline row editing state
  const [editingRowId, setEditingRowId] = useState(null);
  const [rowForm, setRowForm] = useState({});

  const hasLoadedOnce = useRef(false);
  const openAddDialogRef = useRef(null);

  // Sidebar state
  const [isUrlsSidebarOpen, setIsUrlsSidebarOpen] = useState(false);
  const [selectedWebsiteForSidebar, setSelectedWebsiteForSidebar] = useState(null);
  const [isUnifiedUrlsSidebarOpen, setIsUnifiedUrlsSidebarOpen] = useState(false);

  useEffect(() => {
    setPageMeta({ description: "Manage websites used as knowledge sources for this agent." });
    return () => setPageMeta({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep ref current so the TopBar action button always calls the latest openAddDialog
  useEffect(() => {
    openAddDialogRef.current = openAddDialog;
  });

  // Register the "+ Add Website" button in the TopBar actions — only in agent context
  useEffect(() => {
    if (!agentId) return;
    setPageMeta((prev) => ({
      ...prev,
      actions: (
        <Button
          variant="contained"
          onClick={() => openAddDialogRef.current?.()}
          sx={{ textTransform: "none", fontSize: "0.875rem", fontWeight: 500, boxShadow: "none", px: 2, "&:hover": { boxShadow: "none" } }}
        >
          + Add Website
        </Button>
      ),
    }));
  }, [agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data, loading: isDataLoading } = useFetch(
    `/v1/apps/${currentApp.id}/websites?app_name=${params.app_name}&page=${
      queryParams.page
    }&page_size=${queryParams.pageSize}${
      queryParams.searchQuery ? `&query=${queryParams.searchQuery}` : ""
    }`,
    tokens,
    [
      queryParams.page,
      queryParams.pageSize,
      queryParams.searchQuery,
      saved,
      queryParams.refreshKey,
      agentId,
    ],
    {}
  );

  const { sendPostRequest: saveWebsites, loading: savingWebsites } = usePostRequest(
    `/v1/apps/${currentApp.id}/websites/create_and_update?agent_id=${agentId}`,
    tokens
  );

  const { sendPostRequest: sendBulkAction } = usePostRequest(
    `/v1/apps/${currentApp?.id}/websites/`,
    tokens
  );

  const { data: statusCountsData } = useFetch(
    `/v1/apps/${currentApp.id}/urls/status_counts`,
    tokens,
    []
  );

  useEffect(() => {
    if (data) {
      hasLoadedOnce.current = true;
      setWebsites(data.websites || []);
      setTotalCount(data.total_count || 0);
    }
  }, [data]);

  useEffect(() => {
    if (statusCountsData) setStatusCounts(statusCountsData);
  }, [statusCountsData]);


  const refreshData = () =>
    setQueryParams((prev) => ({ ...prev, refreshKey: prev.refreshKey + 1 }));

  const tagOptions = removeDuplicatesFromArray([
    "Development", "Staged", "Production",
    ...(websites || []).reduce((acc, w) => acc.concat(w?.tags || []), []),
    ...(currentApp?.trigger_points || []),
  ]);

  const skipPatternOptions = removeDuplicatesFromArray([
    "calendar", "news", "blog", "contact",
    ...yearsListSince("2000"),
    ...((websites || []).reduce((acc, w) => acc.concat(w?.skip_patterns || []), [])),
  ]);

  const allowedPatternOptions = removeDuplicatesFromArray([
    "/products/", "/services/", "/catalog/", "/category/", "/shop/", "/store/", "/item/",
    ...((websites || []).reduce((acc, w) => acc.concat(w?.allowed_patterns || []), [])),
  ]);

  const openAddDialog = () => {
    setEditingWebsite(null);
    setForm({
      ...EMPTY_FORM,
      tags: agentSiteCode ? [agentSiteCode] : [],
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEditDialog = (website) => {
    setEditingWebsite(website);
    setForm({
      url: website.url || "",
      site_map: website.site_map || "full_website",
      tags: website.tags || [],
      skip_patterns: website.skip_patterns || [],
      allowed_patterns: website.allowed_patterns || [],
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingWebsite(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
  };

  const handleDialogSave = () => {
    const errors = {};
    if (!form.url.trim()) errors.url = true;
    if (!form.site_map) errors.site_map = true;
    if (Object.keys(errors).length) { setFormErrors(errors); return; }

    if (editingWebsite) {
      const id = editingWebsite.id || editingWebsite.tempId;
      setWebsites((prev) =>
        prev.map((w) =>
          w.id === id || w.tempId === id ? { ...w, ...form } : w
        )
      );
      setChanges((prev) => ({ ...prev, [id]: true }));
    } else {
      const tempId = generateTempId();
      setWebsites((prev) => [
        {
          tempId,
          url: form.url,
          site_map: form.site_map,
          tags: form.tags,
          skip_patterns: form.skip_patterns,
          allowed_patterns: form.allowed_patterns,
          user: user,
          app_id: currentApp.id,
          status: "",
          urls_count: 0,
        },
        ...prev,
      ]);
      setChanges((prev) => ({ ...prev, [tempId]: true }));
    }
    handleDialogClose();
  };

  const handleSave = async () => {
    const websitesToSave = websites
      .filter((w) => changes[w.id || w.tempId])
      .map((w) => ({ ...w, app_id: currentApp.id }));
    if (!websitesToSave.length) return;
    const isNewWebsite = websitesToSave.some((w) => w.tempId);
    const successMessage = isNewWebsite
      ? `${websitesToSave.length === 1 ? "Website added" : `${websitesToSave.length} websites added`} to the knowledge base`
      : `${websitesToSave.length === 1 ? "Website updated" : `${websitesToSave.length} websites updated`} successfully`;
    try {
      await saveWebsites({ websites: websitesToSave }, "", { successMessage });
      setChanges({});
      setSaved((prev) => prev + 1);
    } catch (error) {
      console.error("Error saving websites:", error);
    }
  };

  const BULK_ACTION_MESSAGES = {
    to_be_indexed:  "URLs queued for indexing — they'll be crawled shortly",
    to_be_skipped:  "URLs marked to be skipped during the next crawl",
    to_be_deleted:  "URLs marked for deletion",
  };

  const handleBulkAction = async (websiteId, status, extra = {}) => {
    const isForceIndex = extra.force_index;
    const successMessage = isForceIndex
      ? "URLs queued for force re-indexing — existing content will be replaced"
      : BULK_ACTION_MESSAGES[status] || "Action applied successfully";
    await sendBulkAction({ status, ...extra }, `${websiteId}/urls/update_status`, { successMessage });
    await new Promise((r) => setTimeout(r, 500));
    refreshData();
  };

  const startRowEdit = (website, e) => {
    if (e) e.stopPropagation();
    setEditingRowId(website.id || website.tempId);
    setRowForm({
      url: website.url || "",
      site_map: website.site_map || "full_website",
      tags: website.tags || [],
      skip_patterns: website.skip_patterns || [],
      allowed_patterns: website.allowed_patterns || [],
    });
  };

  const arraysEqualIgnoreOrder = (a, b) =>
    JSON.stringify([...(a || [])].sort()) === JSON.stringify([...(b || [])].sort());

  const hasRowFormChanged = (original) =>
    rowForm.url !== (original.url || "") ||
    rowForm.site_map !== (original.site_map || "full_website") ||
    !arraysEqualIgnoreOrder(rowForm.tags, original.tags) ||
    !arraysEqualIgnoreOrder(rowForm.skip_patterns, original.skip_patterns) ||
    !arraysEqualIgnoreOrder(rowForm.allowed_patterns, original.allowed_patterns);

  const saveRowEdit = (website, e) => {
    if (e) e.stopPropagation();
    const websiteId = website.id || website.tempId;
    const rowHasChanges = hasRowFormChanged(website);

    if (rowHasChanges) {
      setWebsites((prev) =>
        prev.map((w) => (w.id === websiteId || w.tempId === websiteId ? { ...w, ...rowForm } : w))
      );
      setChanges((prev) => ({ ...prev, [websiteId]: true }));
    }
    setEditingRowId(null);
  };

  const cancelRowEdit = (e) => {
    if (e) e.stopPropagation();
    setEditingRowId(null);
  };

  const displayedWebsites = websites || [];

  const hasChanges = Object.values(changes).some(Boolean);
  const changeCount = Object.values(changes).filter(Boolean).length;

  // Warn if user tries to close tab or navigate away with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (!hasChanges) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasChanges]);

  if (isDataLoading && !hasLoadedOnce.current) {
    return (
      <LoadingSpinner
        message="Loading knowledge base..."
        subMessage="Fetching websites for this agent"
      />
    );
  }

  return (
    <>
      <Box sx={{ width: "100%", pb: 10 }}>
        {/* Info banner: global view notice or agent context info */}
        {!agentId && (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.25,
              px: 2.5,
              py: 1.5,
              mb: 2,
              borderRadius: "10px",
              bgcolor: SLATE[50],
              border: `1px solid ${SLATE[200]}`,
            }}
          >
            <InfoIcon sx={{ fontSize: "1rem", color: SLATE[400], mt: "2px", flexShrink: 0 }} />
            <Box>
              <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: SLATE[700], lineHeight: 1.4 }}>
                Client-level view — read only
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: SLATE[500], mt: "2px" }}>
                This is a consolidated view of all websites across all agents in this client. To add or manage websites, navigate to a specific agent's Knowledge Base.
              </Typography>
            </Box>
          </Box>
        )}

        {/* Toolbar */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 2 }}>
          <SearchInput
            value={queryParams.searchQuery}
            onChange={(q) => setQueryParams((p) => ({ ...p, searchQuery: q, page: 1 }))}
            placeholder="Search websites..."
          />
          <StatusFilterMenu
            onStatusChange={(status) => { setSelectedStatus(status); setIsUnifiedUrlsSidebarOpen(true); }}
            statusCountsData={statusCounts}
            selectedStatus={selectedStatus}
            buttonText="All URLs"
            menuTitle="Filter URLs by Status"
          />
        </Box>

        {/* Unsaved changes banner */}
        {hasChanges && (
          <Box sx={{
            display: "flex", alignItems: "center", gap: 1.25,
            px: 2.5, py: 1.25, mb: 1.5, borderRadius: "10px",
            bgcolor: "#fffbeb", border: "1px solid #fde68a",
            boxShadow: "0 1px 4px rgba(245,158,11,0.12)",
          }}>
            <WarningIcon sx={{ fontSize: "1.125rem", color: "#d97706", flexShrink: 0 }} />
            <Box>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#92400e", lineHeight: 1.3 }}>
                You have unsaved changes
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "#b45309", mt: "1px" }}>
                {changeCount} {changeCount === 1 ? "website" : "websites"} modified — use the Save button below to apply.
              </Typography>
            </Box>
          </Box>
        )}

        {/* Card */}
        <Box
          sx={{
            bgcolor: "#ffffff",
            border: `1px solid ${SLATE[200]}`,
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05), 0 1px 2px -1px rgba(0,0,0,0.04)",
          }}
        >
          {/* Column Headers */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: COL_TEMPLATE,
              px: 3,
              py: "9px",
              borderBottom: `1px solid ${SLATE[200]}`,
              bgcolor: SLATE[50],
            }}
          >
            {["Website", "URLs", "Scan Mode", "Status", "Tags", "Skip Patterns", "Allowed Patterns", ""].map((col) => (
              <Typography
                key={col}
                sx={{
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  color: SLATE[500],
                  textTransform: "uppercase",
                  letterSpacing: "0.055em",
                }}
              >
                {col}
              </Typography>
            ))}
          </Box>

          {isDataLoading ? (
            <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={24} sx={{ color: INDIGO[500] }} />
            </Box>
          ) : displayedWebsites.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 10 }}>
              <Typography sx={{ fontSize: "2rem", mb: 1 }}>🌐</Typography>
              <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900], mb: "6px" }}>
                {agentId ? "No websites added" : "No websites added yet"}
              </Typography>
              <Typography sx={{ fontSize: "0.875rem", color: SLATE[500] }}>
                {agentId
                  ? "Add websites to build the knowledge base for this agent."
                  : "No websites have been added yet. Go to an agent's Knowledge Base to add websites."}
              </Typography>
              {agentId && (
                <Button
                  variant="contained"
                  onClick={openAddDialog}
                  sx={{ mt: 2, textTransform: "none", fontSize: "0.875rem", fontWeight: 500, boxShadow: "none", "&:hover": { boxShadow: "none" } }}
                >
                  + Add Website
                </Button>
              )}
            </Box>
          ) : (
            <>
              {displayedWebsites.map((website, idx) => {
                const id = website?.id || website?.tempId;
                const isChanged = !!changes[id];
                const isEditing = editingRowId === id;
                return (
                  <Box
                    key={id}
                    onClick={!agentId || isEditing ? undefined : (e) => startRowEdit(website, e)}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: COL_TEMPLATE,
                      px: 3,
                      py: isEditing ? "10px" : "14px",
                      alignItems: "center",
                      borderBottom:
                        idx < displayedWebsites.length - 1
                          ? `1px solid ${SLATE[100]}`
                          : "none",
                      bgcolor: isEditing ? INDIGO[50] : isChanged ? "#fffbeb" : "#ffffff",
                      borderLeft: isEditing ? `3px solid ${INDIGO[400]}` : isChanged ? "3px solid #f59e0b" : "3px solid transparent",
                      transition: "background-color 0.15s, border-color 0.15s",
                      cursor: !agentId || isEditing ? "default" : "pointer",
                      "&:hover": {
                        bgcolor: isEditing ? INDIGO[50] : isChanged ? "#fef3c7" : (agentId ? SLATE[50] : "#ffffff"),
                      },
                    }}
                  >
                    {/* Website URL + added by */}
                    {isEditing ? (
                      <Box sx={{ minWidth: 0, pr: 2 }}>
                        <TrimTextField
                          size="small"
                          fullWidth
                          value={rowForm.url}
                          onChange={(e) => setRowForm((p) => ({ ...p, url: e.target.value }))}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="https://example.com"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "7px",
                              fontSize: "0.875rem",
                              bgcolor: "#fff",
                              "& fieldset": { borderColor: INDIGO[300] },
                              "&:hover fieldset": { borderColor: INDIGO[400] },
                              "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
                            },
                            "& input": { py: "6px" },
                          }}
                        />
                      </Box>
                    ) : (
                      <Box sx={{ minWidth: 0, pr: 2 }}>
                        <Typography
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            color: SLATE[800],
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            lineHeight: 1.4,
                          }}
                          title={website.url}
                        >
                          {website.url || "—"}
                        </Typography>
                        {website.user?.name && (
                          <Typography sx={{ fontSize: "0.72rem", color: SLATE[400], mt: "2px", letterSpacing: "0.01em" }}>
                            by {website.user.name}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* URLs count */}
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: website.urls_count > 0 ? 600 : 400, color: website.urls_count > 0 ? SLATE[700] : SLATE[300] }}>
                      {website.urls_count > 0 ? website.urls_count.toLocaleString() : "—"}
                    </Typography>

                    {/* Scan mode */}
                    {isEditing ? (
                      <Select
                        size="small"
                        value={rowForm.site_map}
                        onChange={(e) => { e.stopPropagation(); setRowForm((p) => ({ ...p, site_map: e.target.value })); }}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          fontSize: "0.8125rem",
                          borderRadius: "7px",
                          bgcolor: "#fff",
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: INDIGO[300] },
                          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: INDIGO[400] },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: INDIGO[500] },
                          "& .MuiSelect-select": { py: "6px" },
                        }}
                      >
                        {SCAN_MODE_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value} sx={{ fontSize: "0.8125rem" }}>{o.title}</MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <Typography sx={{ fontSize: "0.8125rem", color: SLATE[600] }}>
                        {SCAN_MODE_OPTIONS.find((o) => o.value === website.site_map)?.title || website.site_map || "—"}
                      </Typography>
                    )}

                    {/* Status */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <StatusChip status={website.status} />
                      {website.sitemap_diff && (
                        <Tooltip title="Sitemap differences detected">
                          <Box sx={{ width: 7, height: 7, bgcolor: "#ef4444", borderRadius: "50%", flexShrink: 0 }} />
                        </Tooltip>
                      )}
                    </Box>

                    {/* Tags */}
                    {isEditing ? (
                      <Autocomplete
                        multiple
                        freeSolo
                        size="small"
                        options={tagOptions}
                        value={rowForm.tags}
                        onChange={(_, val) => setRowForm((p) => ({ ...p, tags: val }))}
                        onClick={(e) => e.stopPropagation()}
                        renderTags={(val, getTagProps) =>
                          val.map((opt, i) => (
                            <Chip
                              key={opt}
                              label={opt}
                              size="small"
                              {...getTagProps({ index: i })}
                              sx={{ height: 20, fontSize: "0.6875rem", bgcolor: INDIGO[50], color: INDIGO[700], "& .MuiChip-label": { px: "6px" }, "& .MuiChip-deleteIcon": { color: INDIGO[400] } }}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={rowForm.tags.length === 0 ? "Add tags..." : ""}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "7px",
                                fontSize: "0.8125rem",
                                bgcolor: "#fff",
                                "& fieldset": { borderColor: INDIGO[300] },
                                "&:hover fieldset": { borderColor: INDIGO[400] },
                                "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
                              },
                            }}
                          />
                        )}
                      />
                    ) : (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px", overflow: "hidden" }}>
                        {(website.tags || []).slice(0, 2).map((tag) => (
                          <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: "0.6875rem",
                              bgcolor: INDIGO[50],
                              color: INDIGO[600],
                              fontWeight: 500,
                              border: `1px solid ${INDIGO[100]}`,
                              borderRadius: "6px",
                              "& .MuiChip-label": { px: "7px" },
                            }}
                          />
                        ))}
                        {(website.tags || []).length > 2 && (
                          <Typography sx={{ fontSize: "0.75rem", color: SLATE[400], alignSelf: "center" }}>
                            +{website.tags.length - 2}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Skip Patterns — empty cells when editing (content moves to expansion panel) */}
                    {isEditing && <Box />}
                    {isEditing && <Box />}

                    {/* Skip Patterns */}
                    {!isEditing && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px", overflow: "hidden" }}>
                        {website.site_map !== "full_website" ? (
                          <Typography sx={{ fontSize: "0.8125rem", color: SLATE[300] }}>—</Typography>
                        ) : (website.skip_patterns || []).length === 0 ? (
                          <Typography sx={{ fontSize: "0.8125rem", color: SLATE[300] }}>—</Typography>
                        ) : (
                          <>
                            {(website.skip_patterns || []).slice(0, 2).map((p) => (
                              <Chip key={p} label={p} size="small" sx={{ height: 20, fontSize: "0.6875rem", bgcolor: SLATE[100], color: SLATE[600], borderRadius: "6px", "& .MuiChip-label": { px: "7px" } }} />
                            ))}
                            {(website.skip_patterns || []).length > 2 && (
                              <Typography sx={{ fontSize: "0.75rem", color: SLATE[400], alignSelf: "center" }}>
                                +{website.skip_patterns.length - 2}
                              </Typography>
                            )}
                          </>
                        )}
                      </Box>
                    )}

                    {/* Allowed Patterns */}
                    {!isEditing && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px", overflow: "hidden" }}>
                        {website.site_map !== "full_website" ? (
                          <Typography sx={{ fontSize: "0.8125rem", color: SLATE[300] }}>—</Typography>
                        ) : (website.allowed_patterns || []).length === 0 ? (
                          <Typography sx={{ fontSize: "0.8125rem", color: SLATE[300] }}>—</Typography>
                        ) : (
                          <>
                            {(website.allowed_patterns || []).slice(0, 2).map((p) => (
                              <Chip key={p} label={p} size="small" sx={{ height: 20, fontSize: "0.6875rem", bgcolor: SLATE[100], color: SLATE[600], borderRadius: "6px", "& .MuiChip-label": { px: "7px" } }} />
                            ))}
                            {(website.allowed_patterns || []).length > 2 && (
                              <Typography sx={{ fontSize: "0.75rem", color: SLATE[400], alignSelf: "center" }}>
                                +{website.allowed_patterns.length - 2}
                              </Typography>
                            )}
                          </>
                        )}
                      </Box>
                    )}

                    {/* Actions */}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                      {isEditing ? (
                        <>
                          <IconButton
                            size="small"
                            onClick={(e) => saveRowEdit(website, e)}
                            sx={{ color: INDIGO[600], bgcolor: INDIGO[100], borderRadius: "7px", p: "5px", "&:hover": { bgcolor: INDIGO[200] } }}
                          >
                            <CheckIcon sx={{ fontSize: "1rem" }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={cancelRowEdit}
                            sx={{ color: SLATE[500], bgcolor: SLATE[100], borderRadius: "7px", p: "5px", "&:hover": { bgcolor: SLATE[200] } }}
                          >
                            <CloseIcon sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        </>
                      ) : (
                        <OptionMenu
                          key={id + "-options"}
                          options={agentId ? [
                            { value: "view-links", title: "View Links", onClick: () => { setSelectedWebsiteForSidebar(website); setIsUrlsSidebarOpen(true); } },
                            { value: "index-all", title: "Index All", onClick: () => handleBulkAction(website.id, "to_be_indexed") },
                            { value: "force-index", title: "Force Index", onClick: () => handleBulkAction(website.id, "to_be_indexed", { force_index: true }) },
                            { value: "skip-all", title: "Skip All", onClick: () => handleBulkAction(website.id, "to_be_skipped") },
                            { value: "delete-urls", title: "Delete URLs", onClick: () => handleBulkAction(website.id, "to_be_deleted") },
                            { value: "refresh-sitemap", title: "Refresh Sitemap", onClick: async () => { await sendBulkAction({ id: website.id }, `sitemap_refresh`, { successMessage: "Sitemap refresh started — this may take a few minutes" }); refreshData(); } },
                            { value: "divider", title: "—", disabled: true },
                            { value: "advanced", title: "Advanced Settings", onClick: () => openEditDialog(website) },
                          ] : [
                            { value: "view-links", title: "View Links", onClick: () => { setSelectedWebsiteForSidebar(website); setIsUrlsSidebarOpen(true); } },
                          ]}
                        />
                      )}
                    </Box>

                    {/* Skip & Allowed Patterns — full-width expansion when editing */}
                    {isEditing && rowForm.site_map === "full_website" && (
                      <Box
                        sx={{ gridColumn: "1 / -1", pt: 1.5, pb: 0.5, borderTop: `1px dashed ${INDIGO[200]}`, mt: 1 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                          <Box>
                            <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: SLATE[500], textTransform: "uppercase", letterSpacing: "0.05em", mb: "6px" }}>
                              Skip Patterns
                            </Typography>
                            <Autocomplete
                              multiple freeSolo size="small"
                              options={skipPatternOptions}
                              value={rowForm.skip_patterns}
                              onChange={(_, val) => setRowForm((p) => ({ ...p, skip_patterns: val }))}
                              renderTags={(val, getTagProps) =>
                                val.map((opt, i) => (
                                  <Chip key={opt} label={opt} size="small" {...getTagProps({ index: i })}
                                    sx={{ height: 20, fontSize: "0.6875rem", bgcolor: SLATE[100], color: SLATE[700], "& .MuiChip-label": { px: "6px" } }}
                                  />
                                ))
                              }
                              renderInput={(params) => (
                                <TextField {...params} placeholder={rowForm.skip_patterns.length === 0 ? "e.g. news, blog..." : ""}
                                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "7px", fontSize: "0.8125rem", bgcolor: "#fff", "& fieldset": { borderColor: INDIGO[300] }, "&:hover fieldset": { borderColor: INDIGO[400] }, "&.Mui-focused fieldset": { borderColor: INDIGO[500] } } }}
                                />
                              )}
                            />
                            <Typography sx={{ fontSize: "0.6875rem", color: SLATE[400], mt: "4px" }}>
                              URLs containing these patterns will be skipped.
                            </Typography>
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: SLATE[500], textTransform: "uppercase", letterSpacing: "0.05em", mb: "6px" }}>
                              Allowed Patterns
                            </Typography>
                            <Autocomplete
                              multiple freeSolo size="small"
                              options={allowedPatternOptions}
                              value={rowForm.allowed_patterns}
                              onChange={(_, val) => setRowForm((p) => ({ ...p, allowed_patterns: val }))}
                              renderTags={(val, getTagProps) =>
                                val.map((opt, i) => (
                                  <Chip key={opt} label={opt} size="small" {...getTagProps({ index: i })}
                                    sx={{ height: 20, fontSize: "0.6875rem", bgcolor: SLATE[100], color: SLATE[700], "& .MuiChip-label": { px: "6px" } }}
                                  />
                                ))
                              }
                              renderInput={(params) => (
                                <TextField {...params} placeholder={rowForm.allowed_patterns.length === 0 ? "e.g. /products/, /services/..." : ""}
                                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "7px", fontSize: "0.8125rem", bgcolor: "#fff", "& fieldset": { borderColor: INDIGO[300] }, "&:hover fieldset": { borderColor: INDIGO[400] }, "&.Mui-focused fieldset": { borderColor: INDIGO[500] } } }}
                                />
                              )}
                            />
                            <Typography sx={{ fontSize: "0.6875rem", color: SLATE[400], mt: "4px" }}>
                              Only URLs matching these patterns will be indexed.
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })}

              {/* Pagination */}
              <Box sx={{ borderTop: `1px solid ${SLATE[100]}`, bgcolor: SLATE[50] }}>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={queryParams.page - 1}
                  rowsPerPage={queryParams.pageSize}
                  rowsPerPageOptions={[10, 25, 50]}
                  onPageChange={(_, newPage) => setQueryParams((p) => ({ ...p, page: newPage + 1 }))}
                  onRowsPerPageChange={(e) => setQueryParams((p) => ({ ...p, pageSize: parseInt(e.target.value, 10), page: 1 }))}
                  sx={{
                    "& .MuiTablePagination-toolbar": { minHeight: 48 },
                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: "0.8125rem", color: SLATE[500] },
                  }}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
          }}
        >
          <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900] }}>
            {editingWebsite ? "Advanced Settings" : "Add Website"}
          </Typography>
          <IconButton size="small" onClick={handleDialogClose} sx={{ color: SLATE[400] }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{ pt: "8px !important", display: "flex", flexDirection: "column", gap: 2 }}
        >
          {/* URL */}
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              Website URL *
            </Typography>
            <TrimTextField
              fullWidth
              size="small"
              placeholder="https://example.com"
              value={form.url}
              disabled={!!(editingWebsite?.id)}
              onChange={(e) => {
                setForm((p) => ({ ...p, url: e.target.value }));
                setFormErrors((p) => ({ ...p, url: false }));
              }}
              error={formErrors.url}
              helperText={formErrors.url ? "URL is required" : ""}
              sx={INPUT_SX}
            />
          </Box>

          {/* Scan Mode */}
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              Scan Mode *
            </Typography>
            <Select
              fullWidth
              size="small"
              value={form.site_map}
              onChange={(e) => {
                setForm((p) => ({ ...p, site_map: e.target.value }));
                setFormErrors((p) => ({ ...p, site_map: false }));
              }}
              error={formErrors.site_map}
              sx={{
                borderRadius: "8px",
                fontSize: "0.875rem",
                color: SLATE[900],
                "& .MuiOutlinedInput-notchedOutline": { borderColor: SLATE[300] },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: SLATE[400] },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: INDIGO[500] },
              }}
            >
              {SCAN_MODE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.title}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* Tags */}
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              Data Tags
            </Typography>
            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={tagOptions}
              value={form.tags}
              onChange={(_, val) => setForm((p) => ({ ...p, tags: val }))}
              renderTags={(val, getTagProps) =>
                val.map((opt, idx) => (
                  <Chip
                    key={opt}
                    label={opt}
                    size="small"
                    {...getTagProps({ index: idx })}
                    sx={{
                      height: 22,
                      fontSize: "0.75rem",
                      bgcolor: INDIGO[50],
                      color: INDIGO[700],
                      "& .MuiChip-label": { px: "8px" },
                      "& .MuiChip-deleteIcon": { color: INDIGO[400] },
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={form.tags.length === 0 ? "Add tags..." : ""}
                  sx={INPUT_SX}
                />
              )}
            />
          </Box>

          {/* Skip Patterns — full_website only */}
          {form.site_map === "full_website" && (
            <Box>
              <Typography component="label" sx={FIELD_LABEL_SX}>
                Skip Patterns
              </Typography>
              <Autocomplete
                multiple
                freeSolo
                size="small"
                options={skipPatternOptions}
                value={form.skip_patterns}
                onChange={(_, val) => setForm((p) => ({ ...p, skip_patterns: val }))}
                renderTags={(val, getTagProps) =>
                  val.map((opt, idx) => (
                    <Chip
                      key={opt}
                      label={opt}
                      size="small"
                      {...getTagProps({ index: idx })}
                      sx={{
                        height: 22,
                        fontSize: "0.75rem",
                        bgcolor: SLATE[100],
                        color: SLATE[700],
                        "& .MuiChip-label": { px: "8px" },
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={form.skip_patterns.length === 0 ? "e.g. news, blog..." : ""}
                    sx={INPUT_SX}
                  />
                )}
              />
              <Typography sx={{ fontSize: "0.75rem", color: SLATE[400], mt: "4px" }}>
                URLs containing these patterns will be skipped during indexing.
              </Typography>
            </Box>
          )}

          {/* Allowed Patterns — full_website only */}
          {form.site_map === "full_website" && (
            <Box>
              <Typography component="label" sx={FIELD_LABEL_SX}>
                Allowed Patterns
              </Typography>
              <Autocomplete
                multiple
                freeSolo
                size="small"
                options={allowedPatternOptions}
                value={form.allowed_patterns}
                onChange={(_, val) => setForm((p) => ({ ...p, allowed_patterns: val }))}
                renderTags={(val, getTagProps) =>
                  val.map((opt, idx) => (
                    <Chip
                      key={opt}
                      label={opt}
                      size="small"
                      {...getTagProps({ index: idx })}
                      sx={{
                        height: 22,
                        fontSize: "0.75rem",
                        bgcolor: SLATE[100],
                        color: SLATE[700],
                        "& .MuiChip-label": { px: "8px" },
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      form.allowed_patterns.length === 0
                        ? "e.g. /products/, /services/..."
                        : ""
                    }
                    sx={INPUT_SX}
                  />
                )}
              />
              <Typography sx={{ fontSize: "0.75rem", color: SLATE[400], mt: "4px" }}>
                If specified, only URLs matching these patterns will be indexed.
              </Typography>
            </Box>
          )}

          {/* Dialog actions */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 1 }}>
            <Button
              onClick={handleDialogClose}
              variant="outlined"
              size="small"
              sx={{
                borderColor: SLATE[300],
                color: SLATE[700],
                textTransform: "none",
                borderRadius: "8px",
                "&:hover": { borderColor: SLATE[400], bgcolor: SLATE[50] },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDialogSave}
              variant="contained"
              size="small"
              sx={{
                textTransform: "none",
                fontSize: "0.875rem",
                fontWeight: 500,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              {editingWebsite ? "Save Changes" : "Add Website"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* URL Sidebars */}
      {isUrlsSidebarOpen && (
        <UrlsSidebar
          website={selectedWebsiteForSidebar}
          currentApp={currentApp}
          tokens={tokens}
          isOpen={isUrlsSidebarOpen}
          readOnly={!agentId}
          onClose={() => {
            setIsUrlsSidebarOpen(false);
            setSelectedWebsiteForSidebar(null);
          }}
        />
      )}
      {isUnifiedUrlsSidebarOpen && (
        <UnifiedUrlsSidebar
          currentApp={currentApp}
          tokens={tokens}
          isOpen={isUnifiedUrlsSidebarOpen}
          readOnly={!agentId}
          onClose={() => {
            setIsUnifiedUrlsSidebarOpen(false);
            setSelectedStatus("all");
          }}
          selectedStatus={selectedStatus}
          website={null}
        />
      )}

      {/* Bottom Action Bar — only in agent context */}
      {agentId && (
        <BottomActionBar
          disabledSave={!hasChanges || savingWebsites}
          handleSave={handleSave}
        />
      )}
    </>
  );
}
